import { Injectable } from '@angular/core';
import { of, Observable, empty } from 'rxjs';
import {
  catchError,
  map,
  switchMap,
  withLatestFrom,
  pluck,
} from 'rxjs/operators';
import { Store, select } from '@ngrx/store';
import { Actions, Effect, ofType } from '@ngrx/effects';
import { AlertService, CupcakeFlavors, AlertType } from '@ipreo/ngx-sprinkles';
import {
  LoadCurves,
  SelectCurve,
  LoadCurvesSuccess,
  LoadCurvesFail,
  LoadWeeklyCurvesSuccess,
  LoadWeeklyCurvesFail,
  BenchmarkCurvesActionTypes,
} from './actions';
import {
  getBenchmarkCurveForIdentifier,
  NO_MMD_PERMISSIONS_NOTIFICATIONS,
} from '../../helpers/benchmark-curve';
import { BenchmarkService } from '../../services/benchmark.service';
import { selectBenchmarkCurveState } from '../reducer';
import { BenchmarkCurveStoreState } from './state';
import { BenchmarkCurveIdentifier } from '../../types/user-preferences.types';

@Injectable()
export class BenchmarkCurvesEffects {
  @Effect()
  public loadCurves$: Observable<
    LoadCurvesSuccess | LoadCurvesFail
  > = this.actions$.pipe(
    ofType(BenchmarkCurvesActionTypes.LoadCurves),
    withLatestFrom(
      this.store.pipe(select(selectBenchmarkCurveState), pluck('selectedCurve'))
    ),
    switchMap(
      ([action, selectedCurve]: [LoadCurves, BenchmarkCurveIdentifier]) =>
        this.benchmarkService
          .getCurves(action.payload.date, action.payload.provider)
          .pipe(
            map(
              ({ data }) => new LoadCurvesSuccess({ ...action.payload, data })
            ),
            catchError((error) => {
              if (selectedCurve && selectedCurve.startsWith('MMD')) {
                this.alertService.create({
                  body: NO_MMD_PERMISSIONS_NOTIFICATIONS.body,
                  alertType: AlertType.toast,
                  color: CupcakeFlavors.Danger,
                  icon: 'fa fa-exclamation-triangle',
                  timeout: 8000,
                });
              }
              return of(
                new LoadCurvesFail({
                  error: error && (error as Error).message,
                  errorCode: error.status,
                })
              );
            })
          )
    )
  );

  @Effect()
  public loadWeeklyCurves$: Observable<
    LoadWeeklyCurvesSuccess | LoadWeeklyCurvesFail
  > = this.actions$.pipe(
    ofType(BenchmarkCurvesActionTypes.LoadWeeklyCurves),
    switchMap((action: LoadCurves) =>
      this.benchmarkService
        .getWeeklyCurves(action.payload.date, action.payload.provider)
        .pipe(
          map(
            ({ data }) =>
              new LoadWeeklyCurvesSuccess({ ...action.payload, data })
          ),
          catchError((error) =>
            of(
              new LoadWeeklyCurvesFail({
                error: error && (error as Error).message,
                errorCode: error.status,
              })
            )
          )
        )
    )
  );

  @Effect()
  public selectCurve$: Observable<LoadCurves> = this.actions$.pipe(
    ofType(BenchmarkCurvesActionTypes.SelectCurve),
    withLatestFrom(this.store.pipe(select(selectBenchmarkCurveState))),
    switchMap(
      ([action, { single }]: [SelectCurve, BenchmarkCurveStoreState]) => {
        const mapping = getBenchmarkCurveForIdentifier(action.payload);
        if (single.provider !== mapping.provider) {
          return of(
            new LoadCurves({
              date: single.date,
              provider: mapping.provider,
            })
          );
        } else {
          return empty();
        }
      }
    )
  );

  @Effect()
  public updateUserSetting$: Observable<SelectCurve> = this.actions$.pipe(
    ofType('[MuniUserSettings] Update'),
    withLatestFrom(this.store.pipe(select(selectBenchmarkCurveState))),
    switchMap(
      ([action, { selectedCurve }]: [any, BenchmarkCurveStoreState]) => {
        const isDefaultCurveChanged =
          action.payload.userPreference.name === 'defaultBenchmarkCurve';

        if (isDefaultCurveChanged && selectedCurve) {
          return of(new SelectCurve(action.payload.userPreference.value));
        }
        return empty();
      }
    )
  );
  constructor(
    private actions$: Actions,
    private store: Store<Record<string, unknown>>,
    private benchmarkService: BenchmarkService,
    private alertService: AlertService
  ) {}
}
